import React, { useState, useLayoutEffect, useRef } from 'react';
import { Copy, Trash2, Plus, ChevronUp, ChevronDown, ImageIcon } from 'lucide-react';

/**
 * ElementToolbar - Floating toolbar for selected elements in iframe preview
 *
 * @param {Object} props
 * @param {React.RefObject} props.iframeRef - Reference to the iframe element
 * @param {string} props.selectedElementPath - Path of the selected element
 * @param {string} props.selectedSectionId - ID of the selected section
 * @param {number} props.zoomLevel - Current zoom level (for position calculation)
 * @param {Function} props.onDuplicate - Callback to duplicate element
 * @param {Function} props.onDelete - Callback to delete element
 * @param {Function} props.onAddComponent - Callback to add component inside
 * @param {Function} props.onMoveUp - Callback to move element up
 * @param {Function} props.onMoveDown - Callback to move element down
 * @param {string} props.elementType - Type of the selected element (row, column, text, image, etc.)
 * @param {boolean} props.canAddChildren - Whether the element can have children added
 * @param {Function} props.onChangeMedia - Callback to change media (for image elements)
 */
export function ElementToolbar({
  iframeRef,
  selectedElementPath,
  selectedSectionId,
  zoomLevel = 100,
  onDuplicate,
  onDelete,
  onAddComponent,
  onMoveUp,
  onMoveDown,
  elementType = 'element',
  canAddChildren = false,
  onChangeMedia,
}) {
  const [position, setPosition] = useState({ top: 0, left: 0, visible: false });
  const toolbarRef = useRef(null);

  // useLayoutEffect: compute position synchronously before browser paints,
  // so toolbar appears in correct position on the same frame as the React render
  useLayoutEffect(() => {
    if (!iframeRef?.current || (!selectedElementPath && !selectedSectionId)) {
      setPosition(prev => ({ ...prev, visible: false }));
      return;
    }

    const updatePosition = () => {
      const iframe = iframeRef.current;
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!iframeDoc) return;

      let element = null;
      if (selectedElementPath && selectedSectionId) {
        const section = iframeDoc.querySelector(`[data-section-id="${selectedSectionId}"]`);
        if (section) {
          element = section.querySelector(`[data-element-id="${selectedElementPath}"]`);
        }
      } else if (selectedSectionId) {
        element = iframeDoc.querySelector(`[data-section-id="${selectedSectionId}"]`);
      }

      if (!element) {
        setPosition(prev => ({ ...prev, visible: false }));
        return;
      }

      const elementRect = element.getBoundingClientRect();
      const iframeRect = iframe.getBoundingClientRect();
      const scale = zoomLevel / 100;
      const scaledElementTop = elementRect.top * scale;
      const scaledElementLeft = elementRect.left * scale;
      const scaledElementWidth = elementRect.width * scale;

      const containerRect = iframe.parentElement?.parentElement?.getBoundingClientRect();
      if (!containerRect) return;

      const toolbarWidth = toolbarRef.current?.offsetWidth || 200;
      const top = iframeRect.top + scaledElementTop - 40;
      const left = iframeRect.left + scaledElementLeft + (scaledElementWidth / 2) - (toolbarWidth / 2);

      setPosition({
        top: Math.max(containerRect.top + 4, top),
        left: Math.max(containerRect.left + 4, Math.min(left, containerRect.right - toolbarWidth - 4)),
        visible: true,
      });
    };

    updatePosition();

    const container = iframeRef.current?.parentElement?.parentElement;
    if (container) {
      container.addEventListener('scroll', updatePosition);
    }
    window.addEventListener('resize', updatePosition);

    return () => {
      if (container) {
        container.removeEventListener('scroll', updatePosition);
      }
      window.removeEventListener('resize', updatePosition);
    };
  }, [iframeRef, selectedElementPath, selectedSectionId, zoomLevel]);

  if (!position.visible) return null;

  // Format element type for display
  const formatElementType = (type) => {
    if (!type) return 'Element';
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  return (
    <div
      ref={toolbarRef}
      className="element-toolbar"
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 40,
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
        padding: '4px 8px',
        backgroundColor: '#3b82f6',
        borderRadius: '6px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        color: 'white',
        fontSize: '12px',
        fontWeight: 500,
        whiteSpace: 'nowrap',
        userSelect: 'none',
      }}
    >
      {/* Element type label */}
      <span style={{ marginRight: '4px', opacity: 0.9 }}>
        {formatElementType(elementType)}
      </span>

      <div style={{ width: '1px', height: '16px', backgroundColor: 'rgba(255,255,255,0.3)', margin: '0 4px' }} />

      {/* Move up button */}
      {onMoveUp && (
        <button
          onClick={onMoveUp}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '24px',
            height: '24px',
            padding: 0,
            border: 'none',
            borderRadius: '4px',
            backgroundColor: 'transparent',
            color: 'white',
            cursor: 'pointer',
            transition: 'background-color 0.15s',
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.2)'}
          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          title="Move up"
        >
          <ChevronUp size={14} />
        </button>
      )}

      {/* Move down button */}
      {onMoveDown && (
        <button
          onClick={onMoveDown}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '24px',
            height: '24px',
            padding: 0,
            border: 'none',
            borderRadius: '4px',
            backgroundColor: 'transparent',
            color: 'white',
            cursor: 'pointer',
            transition: 'background-color 0.15s',
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.2)'}
          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          title="Move down"
        >
          <ChevronDown size={14} />
        </button>
      )}

      <div style={{ width: '1px', height: '16px', backgroundColor: 'rgba(255,255,255,0.3)', margin: '0 4px' }} />

      {/* Change image button (only for image elements) */}
      {elementType === 'image' && onChangeMedia && (
        <button
          onClick={onChangeMedia}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '24px',
            height: '24px',
            padding: 0,
            border: 'none',
            borderRadius: '4px',
            backgroundColor: 'transparent',
            color: 'white',
            cursor: 'pointer',
            transition: 'background-color 0.15s',
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.2)'}
          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          title="Change image"
        >
          <ImageIcon size={14} />
        </button>
      )}

      {/* Add component button (only for containers) */}
      {canAddChildren && onAddComponent && (
        <button
          onClick={onAddComponent}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '24px',
            height: '24px',
            padding: 0,
            border: 'none',
            borderRadius: '4px',
            backgroundColor: 'transparent',
            color: 'white',
            cursor: 'pointer',
            transition: 'background-color 0.15s',
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.2)'}
          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          title="Add component"
        >
          <Plus size={14} />
        </button>
      )}

      {/* Duplicate button */}
      {onDuplicate && (
        <button
          onClick={onDuplicate}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '24px',
            height: '24px',
            padding: 0,
            border: 'none',
            borderRadius: '4px',
            backgroundColor: 'transparent',
            color: 'white',
            cursor: 'pointer',
            transition: 'background-color 0.15s',
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.2)'}
          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          title="Duplicate"
        >
          <Copy size={14} />
        </button>
      )}

      {/* Delete button */}
      {onDelete && (
        <button
          onClick={onDelete}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '24px',
            height: '24px',
            padding: 0,
            border: 'none',
            borderRadius: '4px',
            backgroundColor: 'transparent',
            color: 'white',
            cursor: 'pointer',
            transition: 'background-color 0.15s',
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.2)'}
          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          title="Delete"
        >
          <Trash2 size={14} />
        </button>
      )}
    </div>
  );
}

export default ElementToolbar;
